/**
 * Mobile Sync Integration
 *
 * This module handles synchronization between the WebView (Legend State)
 * and native keyboard extensions (SQLite cache) via the Signal Queue architecture.
 *
 * Data Flow:
 * 1. EXPORT: WebView (Legend State) → SQLite cache (for keyboard read)
 * 2. SIGNAL: Keyboard → Signal Queue → WebView (Legend State processes)
 */

import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { initDeepLinks } from './deeplink';
import PromptNote, {
  type CachedSnippet,
  type CachedNote,
  type KeyboardSignal,
} from '../plugins/PromptNotePlugin';
import { notes$, getNotesArray, createNote } from '../stores/notes$';
import {
  snippets$,
  toggleFavorite,
  upvoteSnippet,
  downvoteSnippet,
  createSnippet,
} from '../stores/snippets$';
import { auth$ } from '../stores/auth$';

// Debounce timer for export
let exportDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const EXPORT_DEBOUNCE_MS = 300;

// ===== INITIALIZATION =====

/**
 * Initialize mobile sync integration.
 * Should be called once on app startup.
 */
export async function initializeMobileSync(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    console.log('[MobileSync] Not a native platform, skipping initialization');
    return;
  }

  console.log('[MobileSync] Initializing mobile sync...');

  try {
    // 1. Initialize deep link handler (OAuth callbacks + app links)
    await initDeepLinks();

    // 2. Set auth token if logged in
    await syncAuthToken();

    // 3. Process any pending signals from keyboard
    await processKeyboardSignals();

    // 4. Export current data to native cache
    await exportToNative();

    // 5. Listen for new signals from keyboard
    await PromptNote.addListener('signalsPending', async ({ count }) => {
      console.log(`[MobileSync] ${count} pending signals from keyboard`);
      await processKeyboardSignals();
    });

    // 6. Listen for app state changes (foreground/background)
    CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        console.log('[MobileSync] App became active, processing signals and re-exporting...');
        await processKeyboardSignals();
        await exportToNative();
      }
    });

    // 7. Subscribe to Legend State changes for auto-export
    notes$.onChange(() => debouncedExport());
    snippets$.onChange(() => debouncedExport());

    // 8. Subscribe to auth changes to sync token
    auth$.session.onChange(({ value: session }) => {
      if (session) {
        syncAuthToken();
      }
    });

    console.log('[MobileSync] Initialization complete');
  } catch (error) {
    console.error('[MobileSync] Initialization failed:', error);
  }
}

// ===== AUTH TOKEN SYNC =====

/**
 * Sync auth token to native storage for keyboard access.
 */
async function syncAuthToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const session = auth$.session.get();
  if (session) {
    try {
      await PromptNote.setAuthToken({
        token: session.access_token,
        refreshToken: session.refresh_token,
        userId: session.user.id,
        userEmail: session.user.email,
      });
      console.log('[MobileSync] Auth token synced to native');
    } catch (error) {
      console.error('[MobileSync] Failed to sync auth token:', error);
    }
  }
}

// ===== SIGNAL PROCESSING (Keyboard → WebView) =====

/**
 * Process pending signals from keyboard.
 * Signals are actions the user performed in the keyboard that need to be
 * processed through Legend State for proper sync to Supabase.
 */
export async function processKeyboardSignals(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { signals } = await PromptNote.getPendingSignals();
    if (signals.length === 0) return;

    console.log(`[MobileSync] Processing ${signals.length} signals`);

    // Process each signal through Legend State
    for (const signal of signals) {
      await processSignal(signal);
    }

    // Clear processed signals
    await PromptNote.clearSignals({
      signalIds: signals.map((s) => s.id),
    });

    // Re-export to native with updated data
    await exportToNative();

    console.log(`[MobileSync] Processed ${signals.length} signals`);
  } catch (error) {
    console.error('[MobileSync] Signal processing failed:', error);
  }
}

/**
 * Process a single keyboard signal.
 */
async function processSignal(signal: KeyboardSignal): Promise<void> {
  switch (signal.action) {
    case 'toggleFavorite':
      if (signal.snippetId) {
        toggleFavorite(signal.snippetId);
        console.log(`[MobileSync] Toggled favorite for snippet: ${signal.snippetId}`);
      }
      break;

    case 'upvote':
      if (signal.snippetId) {
        upvoteSnippet(signal.snippetId);
        console.log(`[MobileSync] Upvoted snippet: ${signal.snippetId}`);
      }
      break;

    case 'downvote':
      if (signal.snippetId) {
        downvoteSnippet(signal.snippetId);
        console.log(`[MobileSync] Downvoted snippet: ${signal.snippetId}`);
      }
      break;

    case 'recordUsage':
      // Analytics tracking (optional - could be extended)
      if (signal.snippetId && signal.metadata) {
        console.log(`[MobileSync] Snippet used: ${signal.snippetId}`, signal.metadata);
      }
      break;

    case 'requestSync':
      // Force refresh - Legend State handles this automatically
      console.log('[MobileSync] Sync requested from keyboard');
      break;

    case 'createSnippet': {
      // Create a new note+snippet from content captured in the keyboard
      const user = auth$.user.get();
      if (user?.id && signal.metadata) {
        const title = String(signal.metadata.title ?? 'Saved from Keyboard');
        const content = String(signal.metadata.content ?? '');
        if (content) {
          const note = createNote(title, user.id, null);
          if (note) {
            createSnippet(note.id, content, '', user.id, () => {});
            console.log(`[MobileSync] Created note+snippet from keyboard: ${note.id}`);
          }
        }
      }
      break;
    }

    default:
      console.warn(`[MobileSync] Unknown signal action: ${signal.action}`);
  }
}

// ===== EXPORT (WebView → Keyboard) =====

/**
 * Export current data to native SQLite cache for keyboard access.
 */
export async function exportToNative(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    // Get active notes (not archived, not deleted)
    const notes = getNotesArray().filter((n) => !n.archived && !n.deleted);
    const noteMap = new Map(notes.map((n) => [n.id, n]));

    // Get all snippets and filter to active ones with valid parent notes
    const snippetsObj = snippets$.get();
    const snippets: CachedSnippet[] = Object.values(snippetsObj)
      .filter((s) => s && !s.deleted && noteMap.has(s.note_id))
      .map((snippet) => {
        const note = noteMap.get(snippet.note_id)!;
        return {
          id: snippet.id,
          note_id: snippet.note_id,
          user_id: snippet.user_id,
          version: snippet.version,
          title: snippet.title || '',
          content: snippet.content,
          favorite: snippet.favorite || false,
          upvotes: snippet.upvotes || 0,
          downvotes: snippet.downvotes || 0,
          created_at: snippet.created_at,
          updated_at: snippet.updated_at || snippet.created_at,
          note_tags: note.tags || [],
          note_title: note.title,
        };
      });

    // Export snippets to native cache
    const snippetResult = await PromptNote.exportSnippets({ snippets });

    // Export notes for tag filtering
    const cachedNotes: CachedNote[] = notes.map((n) => ({
      id: n.id,
      user_id: n.user_id,
      title: n.title,
      tags: n.tags || [],
      updated_at: n.updated_at,
      pinned: n.pinned || false,
    }));

    const noteResult = await PromptNote.exportNotes({ notes: cachedNotes });

    console.log(
      `[MobileSync] Exported ${snippetResult.count} snippets, ${noteResult.count} notes`
    );
  } catch (error) {
    console.error('[MobileSync] Export failed:', error);
  }
}

/**
 * Debounced export to avoid excessive writes.
 */
function debouncedExport(): void {
  if (exportDebounceTimer) {
    clearTimeout(exportDebounceTimer);
  }
  exportDebounceTimer = setTimeout(() => {
    exportToNative();
    exportDebounceTimer = null;
  }, EXPORT_DEBOUNCE_MS);
}

// ===== CLEANUP =====

/**
 * Clear all mobile data (auth tokens, cache).
 * Should be called on logout.
 */
export async function clearMobileData(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await PromptNote.clearAuth();
    await PromptNote.clearCache();
    console.log('[MobileSync] Cleared mobile data');
  } catch (error) {
    console.error('[MobileSync] Failed to clear mobile data:', error);
  }
}
